/** Core dependenices */
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom';

/** Third party dependencies */
import { Card, Image, Tooltip } from 'antd';
import {
    CameraOutlined,
    CarOutlined,
    CheckCircleFilled,
    ClockCircleOutlined,
    ExperimentOutlined,
    EyeOutlined,
    FireOutlined,
    SafetyCertificateOutlined,
    ShoppingOutlined,
    UserOutlined,
} from '@ant-design/icons';
import moment from 'moment';

/** Local imports and assets */
import BrokenImage from './images/broken-image.png'
import { DETECTION_TYPES } from '../../pages/Analytics/analyticsData';

import './alertCard.css'

// Icons and colours come from the same detection-type table the analytics page
// uses, so a Fire/Smoke card here matches its KPI card there.
const TYPE_ICONS = {
    'Face Recognition': <UserOutlined />,
    'Number Plate': <CarOutlined />,
    'Baggage Detection': <ShoppingOutlined />,
    'PPE Detection': <SafetyCertificateOutlined />,
    'FireSmoke Detection': <FireOutlined />,
    'ForeignMaterial Detection': <ExperimentOutlined />,
};

const TYPE_COLORS = DETECTION_TYPES.reduce(
    (acc, type) => ({ ...acc, [type.key]: type.color }),
    {}
);

const FALLBACK_COLOR = '#8c8c8c';

const isUnresolved = (name) => name === 'Not Detected' || name === 'Unknown';

function AlertCard(props) {

    const history = useHistory();

    const [events, setEvents] = useState({});
    const [imageSource, setImageSource] = useState(null);

    useEffect(() => {
        try {
            const { data } = props;

            setEvents(data);
            // Try to load the image - attempt both jpg and png
            if (data?.imgPath) {
                const imageName = data.imgPath.split(';')[0];
                try {
                    // Try JPG first
                    setImageSource(require(`../../static/Images/${imageName}.jpg`));
                } catch (e) {
                    try {
                        // Fall back to PNG
                        setImageSource(require(`../../static/Images/${imageName}.png`));
                    } catch (e2) {
                        setImageSource(BrokenImage);
                    }
                }
            } else {
                setImageSource(BrokenImage);
            }
        } catch (exc) {
            setImageSource(BrokenImage)
            console.log(exc);
        }
    }, [props.data]);

    const onClickView = () => {
        localStorage.setItem('eventId', `event-${props.index}`)
        history.push({
            pathname: './details',
            state: events
        })
    }

    const kpiType = props?.data?.kpiType;
    const color = TYPE_COLORS[kpiType] || FALLBACK_COLOR;
    const manual = props?.data?.manualEntry == "True";
    const validated = props?.data?.validatedEntry == "True";

    const detectionName = props?.data?.detectionName;
    const baggageNote = kpiType === 'Baggage Detection'
        ? (props?.data?.hasWpn == "True" ? ' (Has Baggage)' : ' (No Baggage)')
        : '';

    const date = moment(events.detectionDate, "DD/MM/YYYY");
    const time = moment(events?.startTime, "HH:mm:ss");

    return (
        <div id={`event-${props?.index}`} className='alertCard'>
            <Card bordered={false} onClick={onClickView}>
                <div className='alert-card-media'>
                    <Image
                        className='cover-image'
                        style={{ objectPosition: kpiType === "Number Plate" ? 'bottom' : 'top' }}
                        alt='No Image found'
                        src={imageSource || BrokenImage}
                        preview={false}
                        height={220}
                        width='100%'
                    />
                    <span className='alert-card-tag'>
                        {manual ? 'Manual Entry' : 'Detected by AI'}
                        {validated && (
                            <Tooltip title='Validated'>
                                <CheckCircleFilled style={{ color: '#219653' }} />
                            </Tooltip>
                        )}
                    </span>
                </div>

                <div className='alert-card-content'>
                    <div className='alert-card-head'>
                        <div className='alert-card-type'>{kpiType || 'Detection'}</div>
                        <div
                            className='alert-card-icon'
                            style={{ backgroundColor: `${color}1A`, color }}
                        >
                            {TYPE_ICONS[kpiType]}
                        </div>
                    </div>

                    <div className={`alert-card-name ${isUnresolved(detectionName) ? 'is-unresolved' : ''}`}>
                        {detectionName || 'N/A'}{baggageNote}
                    </div>

                    <div className='alert-card-meta'>
                        <div className='alert-card-meta-row'>
                            <CameraOutlined />
                            <span>{props?.data?.cameraName || 'N/A'}</span>
                        </div>
                        <div className='alert-card-meta-row'>
                            <ClockCircleOutlined />
                            <span>
                                {date.isValid() ? date.format('Do MMMM, YYYY') : 'N/A'}
                                {time.isValid() && ` · ${time.format('hh:mm:ss A')}`}
                            </span>
                        </div>
                    </div>
                </div>

                <div className='alert-card-footer'>
                    <EyeOutlined /> View Alert
                </div>
            </Card>
        </div>
    )
}

export default AlertCard
